import React, { ComponentProps, ReactNode, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { RadioGroupProps } from '@mui/material';

import {
  withTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from '@sbv-clients/ui-shared';

import { useFormFieldError } from '../../hooks/use-field-error';

export type RadioOption<T extends string | number = string | number> = {
  value: T;
  label: ReactNode;
  disabled?: boolean;
};

type RenderType = ComponentProps<typeof Controller>['render'];

type FormRadioGroupAdditionalProps<T extends string | number> = {
  name: string;
  label?: ReactNode; // FormLabel content
  options: RadioOption<T>[];

  showHelperText?: boolean;
  helperText?: ReactNode; // custom helper text (non-error)
  infoText?: ReactNode; // another helper line if you want

  /**
   * If your form state stores a number but MUI returns string from event,
   * we convert it back.
   */
  valueType?: 'string' | 'number';

  /**
   * Same pattern as FormTextField: allow extra onChange/onBlur side effects
   */
  onChange?: (value: T) => void;
  onBlur?: () => void;
};

export type FormRadioGroupProps<T extends string | number = string | number> =
  Omit<RadioGroupProps, 'name' | 'value' | 'onChange'> &
    FormRadioGroupAdditionalProps<T>;

const toTypedValue = <T extends string | number>(
  raw: string,
  valueType: 'string' | 'number'
): T => {
  if (valueType === 'number') {
    // If empty or NaN, let RHF keep it as-is (or you can decide a fallback)
    const n = Number(raw);
    return (Number.isNaN(n) ? (raw as unknown as T) : (n as unknown as T)) as T;
  }
  return raw as unknown as T;
};

const BaseFormRadioGroup = <T extends string | number = string | number>({
  name,
  label,
  options,
  valueType = 'string',
  row,
  sx,
  showHelperText = true,
  helperText: helperTextProp,
  infoText,
  onChange,
  onBlur,
  ...radioGroupProps
}: FormRadioGroupProps<T>) => {
  const { error, helperText } = useFormFieldError(name);

  const mergedHelperText = useMemo(() => {
    // Prefer error helperText; otherwise use provided helperText
    return helperText || helperTextProp;
  }, [helperText, helperTextProp]);

  const render: RenderType = useCallback(
    ({ field }) => {
      // MUI RadioGroup expects string value; it will compare as string.
      // We'll pass stringified value to RadioGroup, and cast back on change.
      const fieldValue = field.value;

      const radioGroupValue =
        fieldValue === null || fieldValue === undefined
          ? ''
          : String(fieldValue);

      return (
        <FormControl sx={sx} error={error}>
          {label ? <FormLabel>{label}</FormLabel> : null}

          <RadioGroup
            {...radioGroupProps}
            row={row}
            value={radioGroupValue}
            onChange={(e) => {
              const typed = toTypedValue<T>(e.target.value, valueType);
              field.onChange(typed);
              onChange?.(typed);
            }}
            onBlur={() => {
              field.onBlur();
              onBlur?.();
            }}
          >
            {options.map((opt) => (
              <FormControlLabel
                key={String(opt.value)}
                value={String(opt.value)}
                control={<Radio />}
                label={opt.label}
                disabled={opt.disabled}
              />
            ))}
          </RadioGroup>

          {Boolean(infoText) && <FormHelperText>{infoText}</FormHelperText>}

          {showHelperText && Boolean(mergedHelperText) && (
            <FormHelperText error={error}>{mergedHelperText}</FormHelperText>
          )}
        </FormControl>
      );
    },
    [
      error,
      infoText,
      label,
      mergedHelperText,
      name,
      onBlur,
      onChange,
      options,
      radioGroupProps,
      row,
      showHelperText,
      sx,
      valueType,
    ]
  );

  return <Controller name={name} render={render} />;
};

export const FormRadioGroup = withTitle(BaseFormRadioGroup);
export const FormRadioGroupWithTitle = withTitle(BaseFormRadioGroup);
